/**
 * @returns the full names of weekdays starting from Sunday.
 * e.g., "Sunday", "Monday", etc.
 */
export function getWeekdays(options?: { lang?: string }): string[] {
  // Create a DateTimeFormat object for the specified language
  const formatter = new Intl.DateTimeFormat(options?.lang, {
    weekday: 'long',
  })

  // Get the current date
  const now = new Date()

  // Find the Sunday of the current week
  const firstDayOfWeek = new Date(now)
  firstDayOfWeek.setDate(now.getDate() - now.getDay()) // Adjust to Sunday

  // Generate an array of weekday names
  return Array.from(Array(7)).map((_, i) => {
    const date = new Date(firstDayOfWeek)
    date.setDate(firstDayOfWeek.getDate() + i)
    return formatter.format(date)
  })
}

/**
 * @returns the current system timezone.
 */
export function getTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * Converts a date from a specified time zone to UTC.
 */
export function toUTCDate(date: Date, timezone: string): Date {
  const localDate = new Date(
    date.toLocaleString('en-US', { timeZone: timezone }),
  )
  const offset = date.getTime() - localDate.getTime()
  return new Date(date.getTime() + offset)
}

/**
 * Converts a UTC date to a specified time zone.
 */
export function toTimezoneDate(date: Date, timezone: string): Date {
  const localDate = new Date(
    date.toLocaleString('en-US', { timeZone: timezone }),
  )
  const offset = localDate.getTime() - date.getTime()
  return new Date(date.getTime() + offset)
}

/**
 * @returns A Date object representing the converted time in the target timezone.
 */
export function convertDateStringToNewTimezoneDate(
  time: string,
  fromTimezone: string,
  toTimezone: string,
): Date {
  const now = new Date()
  const [hours, minutes, seconds] = time.split(':').map(Number)

  const date = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    hours,
    minutes,
    seconds,
  )
  const utcDate = toUTCDate(date, fromTimezone)
  const zonedDate = toTimezoneDate(utcDate, toTimezone)

  return zonedDate
}
